import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Activity, AlertTriangle, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import useMediaQuery from "@/lib/useMediaQuery";
import { teethAreAdjacent } from "@/lib/fdi";
import { isWholeToothProcedure } from "@/lib/procedureFlags";
import ToothChart from "@/components/charting/ToothChart";
import ChartFindingsLegend from "@/components/charting/ChartFindingsLegend";
import { flattenPaletteFindings } from "@/components/charting/FindingsPalette";
import { defaultDentitionFromDOB } from "@/components/charting/dentition";
import { EMPTY_STATE } from "@/components/charting/chartState";
import ProceduresPalette from "./ProceduresPalette";
import TxPlanBuilder from "./TxPlanBuilder";
import PlanTabsRow from "./PlanTabsRow";
import TxPlanCategoryLegend from "./TxPlanCategoryLegend";
import FeatureAccessGate from "@/components/billing/FeatureAccessGate";
import { procedureColor, classifyMultiTooth } from "./procedureColors";
import { buildTreatmentPlanPdf, pdfFilename } from "@/lib/treatmentPlanPdf";
import { computePhaseDurations } from "@/lib/planVisits";
import { treatmentPlanDoctorGate } from "@/lib/treatmentPlanDoctorGate";
import { prewarmEmailBackend } from "@/lib/apiClient";
import {
  checkUnassignedBlock,
  clinicSettingsWithLogo,
  emailTreatmentPlanPdf,
} from "@/lib/treatmentPlanDelivery";
import SchedulePlanItemDialog from "./SchedulePlanItemDialog";
import ComparePlansDialog from "./ComparePlansDialog";
import MarkAcceptedDialog from "./MarkAcceptedDialog";
import CaseTimelineCard from "./CaseTimelineCard";
import ClosePlanDialog from "./ClosePlanDialog";
import CollectPaymentDialog from "./CollectPaymentDialog";
import BuildMultiToothDialog from "./BuildMultiToothDialog";
import ToothNoteDialog from "@/components/charting/ToothNoteDialog";
import { APPROVED_PROCEDURE_CATEGORIES } from "@/lib/procedureCategories";
import { treatmentPlanAuthoringPlans } from "@/lib/mvImport";
import SignatureDialog from "@/components/documents/SignatureDialog";
import {
  getTreatmentPlanDocumentStatus,
  signTreatmentPlanDocument,
  signatureForPdf,
} from "@/lib/signedDocuments";

// Build a chart-state-shaped object from plan items. Each non-declined
// item becomes a synthetic "finding" on its tooth using a code prefixed
// with `__tx_<procedure_id>` so it can't collide with real findings.
// Whole-tooth items paint all 5 surfaces; surface items paint just that
// surface. The corresponding entry in the combined catalog gives the
// color (per procedure category).
const ALL_SURFACES = ["M","O","D","B","L"];

// Whole-tooth detection — primary source is the explicit per-
// procedure surface_specific flag (Settings → Services). Legacy
// fallback kicks in only for rows whose RPC payload predates the
// 20260519000005 migration. Drop the fallback after every clinic
// is migrated.
// Whole-tooth detection lives in @/lib/procedureFlags — shared with
// Doctor.jsx, catalogue-driven via procedures.surface_specific with
// a legacy regex fallback for pre-migration rows.
// Build a one-line summary of the procedures already on a given tooth,
// used as a context hint inside the per-tooth note dialog so the
// dentist isn't typing blind. Whole-tooth items are listed without a
// surface tag; surface items group by procedure ("Composite (M, O)").
function toothContextLine(plan, toothNumber) {
  const t = String(toothNumber);
  const items = (plan?.items ?? []).filter(
    (i) => String(i.tooth_number) === t && i.status !== "Declined",
  );
  if (items.length === 0) return null;
  const byProc = {};
  for (const it of items) {
    const name = it.procedure_name || "Procedure";
    byProc[name] = byProc[name] || [];
    if (it.surface) byProc[name].push(it.surface);
  }
  return Object.entries(byProc)
    .map(([name, surfaces]) => surfaces.length > 0 ? `${name} (${surfaces.join(", ")})` : name)
    .join(", ");
}

// Walk the latest finalized chart snapshot's state.teeth and harvest
// every per-tooth finding note. Returns { "13": "Deep caries on M…",
// "16": "…" } so TreatmentTab can show those as read-only context in
// the plan-note dialog. Empty notes are skipped — never returns a key
// with an empty string.
function extractChartNotesByFdi(snapshotState) {
  const out = {};
  const teeth = snapshotState?.teeth;
  if (!teeth || typeof teeth !== "object") return out;
  for (const [fdi, t] of Object.entries(teeth)) {
    const findings = Array.isArray(t?.findings) ? t.findings : [];
    for (const f of findings) {
      if (f?.finding_code === "note") {
        const text = (f.notes || "").trim();
        if (text) { out[fdi] = text; break; }
      }
    }
  }
  return out;
}

// Per-tooth notes are layered into the chart state as `note` findings
// so the existing Tooth note-pill renders automatically. Click handler
// fires onOpenNote(fdi); the parent uses that to open the popup
// modal anchored over the chart.
function planItemsToChartState(items, toothNotes) {
  const hasItems = !!items?.length;
  const hasNotes = !!(toothNotes && Object.values(toothNotes).some((v) => v));
  if (!hasItems && !hasNotes) return EMPTY_STATE;
  const teeth = {};
  for (const it of (items || [])) {
    if (it.status === "Declined") continue;
    // Multi-tooth items (Bridge / Splint / RPD) carry their teeth in
    // the `teeth` jsonb. Paint EVERY tooth in that array — gives the
    // dentist a continuous strip across 16-15-14-13 instead of a
    // ghost item that doesn't appear on the chart at all.
    const teethArr = Array.isArray(it.teeth)
      ? it.teeth.map((t) => (typeof t === "object" ? t?.tooth_number : t)).filter(Boolean)
      : [];
    const targets = teethArr.length > 1
      ? teethArr.map(String)
      : (it.tooth_number ? [String(it.tooth_number)] : []);
    if (targets.length === 0) continue;
    for (const tooth of targets) {
    if (!teeth[tooth]) teeth[tooth] = { findings: [] };
    // Bundling (20260523000001) stores multi-surface composites as a
    // single normalized string ("MD", "MOD", etc) on one row. The
    // chart's surfaceColors map is keyed by single characters — so we
    // must split the bundle into its component surfaces, otherwise
    // 2+ surface items render as nothing (key "MD" never matched).
    teeth[tooth].findings.push({
      finding_code: `__tx_${it.procedure_id}`,
      // Multi-tooth items have no surface (they cover the whole tooth);
      // single-tooth items may have a bundled surface string ("MD").
      surfaces: teethArr.length > 1
        ? ALL_SURFACES
        : (it.surface ? Array.from(String(it.surface)) : ALL_SURFACES),
    });
    }
  }
  for (const [fdi, text] of Object.entries(toothNotes || {})) {
    if (!text) continue;
    if (!teeth[fdi]) teeth[fdi] = { findings: [] };
    // resolveToothVisual reads `f.notes` (matching chartState.setNote);
    // a stale `f.note` here meant the chart pill never lit amber even
    // though the note WAS persisted. Match the canonical shape.
    teeth[fdi].findings.push({ finding_code: "note", surfaces: [], notes: text });
  }
  return { version: 1, teeth, bridges: [] };
}

function chartStateWithPlanNotes(snapshotState, toothNotes) {
  const base = snapshotState || EMPTY_STATE;
  const hasNotes = !!(toothNotes && Object.values(toothNotes).some((v) => String(v || "").trim()));
  if (!hasNotes) return base;
  const teeth = { ...(base.teeth || {}) };
  for (const [fdi, rawText] of Object.entries(toothNotes || {})) {
    const text = String(rawText || "").trim();
    if (!text) continue;
    const key = String(fdi);
    const existing = teeth[key] || { findings: [] };
    const withoutPlanNote = (existing.findings || []).filter((f) => f.finding_code !== "note");
    teeth[key] = {
      ...existing,
      findings: [...withoutPlanNote, { finding_code: "note", surfaces: [], notes: text }],
    };
  }
  return { ...base, teeth };
}

// =====================================================================
// TreatmentTab — Round 1.
// =====================================================================
// Single-page workflow:
//   1. PlanTabsRow at top: switch between plans, +New Plan.
//   2. Chart canvas: shows the latest finalized chart ghosted, painted
//      with plan-item dots when a plan has work for a tooth.
//   3. ProceduresPalette: sidebar (desktop ≥xl) or bottom-sheet trigger
//      (narrower screens). Tap to "arm" a procedure; subsequent taps
//      on the chart add a plan item.
//   4. TxPlanBuilder below: items grouped by tooth, total, send button,
//      per-item status actions for accepted plans.
// =====================================================================

// Embeddable: when `chartless` is true the medical-history banner +
// chart + paint palette are skipped (the embedder owns those — Doctor
// view's chairside chart does the painting). Plan tabs + builder +
// dialogs still render. `onActivePlanIdChange` lets the embedder
// follow the user's plan tab selection so its own paint flow targets
// the same plan. forwardRef exposes { refresh } so the embedder can
// re-pull plans + active plan after writing through its own paint
// path (chairside add_plan_item / delete_plan_item).
const TreatmentTabBody = forwardRef(function TreatmentTab(
  { patientId, patient, chartless = false, chartFirst = false, onActivePlanIdChange = null, onActivePlanDataChange = null, chartStateOverride = null, hideMultiTooth = false, tutorialDemoData = null, currentAppointmentId = null, onPlanStartedToday = null, hidePlanTabs = false, onPlansChange = null },
  ref,
) {
  const { clinicRole, activeClinic } = useAuth();
  const tutorialDemoMode = !!tutorialDemoData;
  const canPropose = ["clinic_admin","doctor"].includes(clinicRole);
  const canAccept  = ["clinic_admin","doctor"].includes(clinicRole);
  const canEdit    = tutorialDemoMode ? true : ["clinic_admin","doctor"].includes(clinicRole);
  const isDesktop  = useMediaQuery("(min-width: 1280px)");

  const [plans, setPlans]             = useState([]);
  const [procedures, setProcedures]   = useState([]);
  const [findings, setFindings]       = useState([]);     // findings catalog for ghosted-chart colors
  const [paletteCategories, setPaletteCategories] = useState([]);
  const [snapshot, setSnapshot]       = useState(null);
  // Clinic-wide letterhead fields used by the Tx Plan PDF + propose
  // email. Loaded once via get_clinic_settings (fields edited at
  // /settings/clinic). Logo embedding is staged here as a base64 data
  // URL so jsPDF's addImage can render it directly.
  const [clinicForPdf, setClinicForPdf] = useState(null);
  // Clinic-wide chair setup time (minutes) — used by the Tx Plan
  // builder's appointment/phase total, not by each procedure row.
  // Defaults to 15 if the appointment-settings load fails or the
  // column is unset (matches the SQL default).
  const [chairSetupMinutes, setChairSetupMinutes] = useState(15);
  // Latest medical history for this patient — drives the liability
  // banner above the chart so a dentist can't propose treatment without
  // a current MH on file. Loaded with the rest of the tab data.
  const [medicalHistory, setMedicalHistory] = useState(null);
  const [activePlanId, setActivePlanId] = useState(null);

  // Tx Plan Center deep-link: /patients/:id?tab=treatment&plan=<uuid>
  // Captured once on mount via ref so reload() can read it without
  // re-running every time URL params change. Cleared after first apply
  // so subsequent navigations within this patient honor user choice.
  const [searchParams] = useSearchParams();
  const deepLinkPlanIdRef = useRef(searchParams.get("plan"));
  const [activePlan, setActivePlan]   = useState(null);
  const [armed, setArmed]             = useState(null);   // { kind: 'procedure'|'erase'|'multi_tooth', id? } | null
  const [multiToothBuffer, setMultiToothBuffer] = useState([]); // FDI strings selected in multi-tooth mode
  const [scheduleFor, setScheduleFor]   = useState(null);  // plan item being scheduled
  const [schedulePhase, setSchedulePhase] = useState(null); // { items: [...], title } for "Schedule whole phase"
  const [compareOpen, setCompareOpen] = useState(false);    // "Compare with another plan" picker
  const [markAcceptedOpen, setMarkAcceptedOpen] = useState(false); // lifecycle v2 Mark Accepted dialog
  // Selection handed up from the inline ProposalAcceptanceChecklist:
  // null = accept all, [] = reject all, [ids] = subset. `undefined` means
  // the dialog wasn't opened from the checklist (keeps accept-all's null
  // unambiguous).
  const [pendingAcceptedIds, setPendingAcceptedIds] = useState(undefined);
  const [closeOpen, setCloseOpen]     = useState(false);    // S4 Final-Visit close dialog
  const [collectOpen, setCollectOpen] = useState(false);    // intermediate / AR payment dialog
  const [multiToothOpen, setMultiToothOpen] = useState(false); // Multi-Tooth Tool dialog
  const [addProcedureOpen, setAddProcedureOpen] = useState(null); // { name }
  const [planBalance, setPlanBalance] = useState(null);     // {balance_centavos, total_centavos, ...} for active plan
  // Tooth-note popup modal — opened from the chart's note pill OR from
  // the pencil/Add-note buttons in TxPlanBuilder. Holds the tooth_number
  // (string) being edited, or null when the modal is closed.
  const [noteModalTooth, setNoteModalTooth] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [pdfDoctorError, setPdfDoctorError] = useState(null);
  const [busy, setBusy]       = useState({});             // { create_plan, propose, item: { ... } }
  const [signatureDocument, setSignatureDocument] = useState(null);
  const [signingPlan, setSigningPlan] = useState(null);
  const [signatureBusy, setSignatureBusy] = useState(false);

  const dentitionMode = defaultDentitionFromDOB(patient?.date_of_birth);

  const applyTutorialDemoData = useCallback(() => {
    if (!tutorialDemoData) return;
    const nextPlans = tutorialDemoData.plans ?? [];
    const nextActivePlan = tutorialDemoData.activePlan ?? nextPlans[0] ?? null;
    setPlans(nextPlans);
    setProcedures(tutorialDemoData.procedures ?? []);
    setFindings(tutorialDemoData.findings ?? []);
    setPaletteCategories(tutorialDemoData.paletteCategories ?? []);
    setSnapshot(tutorialDemoData.snapshot ?? null);
    setClinicForPdf(tutorialDemoData.clinicForPdf ?? null);
    setChairSetupMinutes(Number(tutorialDemoData.chairSetupMinutes ?? 15));
    setMedicalHistory(tutorialDemoData.medicalHistory ?? null);
    setActivePlanId(tutorialDemoData.activePlanId ?? nextActivePlan?.id ?? null);
    setActivePlan(nextActivePlan);
    setPlanBalance(tutorialDemoData.planBalance ?? null);
    setLoading(false);
    setError(null);
    setBusy({});
  }, [tutorialDemoData]);

  useEffect(() => {
    if (!tutorialDemoMode) return;
    applyTutorialDemoData();
  }, [tutorialDemoMode, applyTutorialDemoData]);

  const blockTutorialDemoMutation = useCallback(() => {
    if (!tutorialDemoMode) return false;
    toast.info("Tutorial demo only. No records are changed.");
    return true;
  }, [tutorialDemoMode]);

  // ----- undo / redo (operation-level) -----
  // V1 covers non-destructive ops only: status changes, phase changes
  // (item / tooth / rename), schedule / unschedule (item / whole-phase).
  // Add and delete are NOT undoable: re-creating a deleted plan-item
  // produces a NEW DB id, breaking redo references. If users delete by
  // mistake they re-add manually. Stack capped at 50, reset when the
  // active plan changes.
  const [undoOps, setUndoOps]   = useState([]);
  const [redoOps, setRedoOps]   = useState([]);
  const [undoBusy, setUndoBusy] = useState(false);

  useEffect(() => {
    setUndoOps([]);
    setRedoOps([]);
    // Drop any sticky tool state from the previous plan so a stale
    // armed eraser / procedure doesn't fire silent clicks on the new plan.
    setArmed(null);
  }, [activePlanId]);

  const recordOp = useCallback((op) => {
    setUndoOps((s) => {
      const next = [...s, op];
      return next.length > 50 ? next.slice(next.length - 50) : next;
    });
    setRedoOps([]);
  }, []);

  const onUndoTx = useCallback(async () => {
    if (undoOps.length === 0 || undoBusy) return;
    const op = undoOps[undoOps.length - 1];
    setUndoBusy(true);
    try {
      await op.undo();
      setUndoOps((s) => s.slice(0, -1));
      setRedoOps((s) => [...s, op]);
    } catch (e) {
      toast.error(`Undo failed${e?.message ? `: ${e.message}` : "."}`);
    } finally {
      setUndoBusy(false);
    }
  }, [undoOps, undoBusy]);

  const onRedoTx = useCallback(async () => {
    if (redoOps.length === 0 || undoBusy) return;
    const op = redoOps[redoOps.length - 1];
    setUndoBusy(true);
    try {
      await op.redo();
      setRedoOps((s) => s.slice(0, -1));
      setUndoOps((s) => [...s, op]);
    } catch (e) {
      toast.error(`Redo failed${e?.message ? `: ${e.message}` : "."}`);
    } finally {
      setUndoBusy(false);
    }
  }, [redoOps, undoBusy]);

  // Keyboard shortcut: Cmd+Z / Cmd+Shift+Z (Ctrl+Z / Ctrl+Y on Win).
  // Only active when treatment is the focused tab — we don't gate by
  // tab state here, but inputs/textareas suppress so native field undo wins.
  useEffect(() => {
    function onKey(e) {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      const key = e.key.toLowerCase();
      if (key === "z" && !e.shiftKey)            { e.preventDefault(); onUndoTx(); }
      else if ((key === "z" && e.shiftKey) || key === "y") { e.preventDefault(); onRedoTx(); }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onUndoTx, onRedoTx]);

  // After every undo/redo finishes the live plan state is stale.
  // Refresh once busy flips back to false. We deliberately depend ONLY
  // on undoBusy so this fires on the true→false transition; including
  // undoOps/redoOps would also re-fire whenever any other handler pushes
  // an op (which already calls refreshActive itself), causing redundant
  // round-trips. refreshActive is captured via closure (declared further
  // down in this component, stable via useCallback).
  useEffect(() => {
    if (!undoBusy && (undoOps.length > 0 || redoOps.length > 0)) {
      refreshActive();
    }
  }, [undoBusy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Synthetic chart state derived from the active plan's items — paints
  // each item as a colored fill on its tooth/surface. Combined catalog
  // adds one entry per procedure so ToothChart's color resolver picks
  // up the procedure's category color.
  const txState = useMemo(() => {
    const base = planItemsToChartState(activePlan?.items, activePlan?.tooth_notes);
    if (multiToothBuffer.length === 0) return base;
    // Overlay synthetic "selected" finding for each tooth in the
    // multi-tooth buffer. The Tooth renderer paints all surfaces
    // with the matching color from combinedFindings, giving the
    // dentist a visual capsule of the current multi-tooth selection.
    const teeth = { ...(base.teeth || {}) };
    for (const fdi of multiToothBuffer) {
      const key = String(fdi);
      const existing = teeth[key] || { findings: [] };
      teeth[key] = {
        ...existing,
        findings: [
          ...(existing.findings || []),
          { finding_code: "__multi_tooth_pending", surfaces: ["M","O","D","B","L"] },
        ],
      };
    }
    return { ...base, teeth };
  }, [activePlan, multiToothBuffer]);
  const txChartBaseState = useMemo(
    () => chartStateWithPlanNotes(chartStateOverride ?? snapshot?.state, activePlan?.tooth_notes),
    [chartStateOverride, snapshot, activePlan?.tooth_notes],
  );
  // Chart finding notes (from the latest finalized snapshot) — surfaced
  // as read-only context inside the plan-note dialog so the dentist can
  // see clinical observations while writing planning-specific notes.
  // These notes do NOT auto-populate plan.tooth_notes — they're a
  // separate layer (see migration 20260517000010).
  const chartNotesByFdi = useMemo(
    () => extractChartNotesByFdi(chartStateOverride ?? snapshot?.state),
    [chartStateOverride, snapshot],
  );
  // Set of FDIs marked Missing on the latest finalized chart. Threaded
  // into BuildMultiToothDialog so its role-marker auto-defaults a
  // missing tooth to "pontic" (clinical truth — you cannot crown air).
  // The doctor can still flip any tooth manually with one click.
  const missingFdiSet = useMemo(() => {
    const teeth = (chartStateOverride ?? snapshot?.state)?.teeth;
    if (!teeth || typeof teeth !== "object") return new Set();
    const out = new Set();
    for (const [fdi, t] of Object.entries(teeth)) {
      const findings = Array.isArray(t?.findings) ? t.findings : [];
      if (findings.some((f) => f?.finding_code === "missing")) {
        out.add(String(fdi));
      }
    }
    return out;
  }, [chartStateOverride, snapshot]);
  const combinedFindings = useMemo(() => {
    const byCode = new Map();
    for (const f of findings) byCode.set(f.code, f);
    for (const f of flattenPaletteFindings(paletteCategories)) {
      byCode.set(f.code, { ...byCode.get(f.code), ...f });
    }
    const synthetic = procedures.map((p) => ({
      code: `__tx_${p.id}`,
      color_hex: procedureColor(p),
    }));
    return [
      ...Array.from(byCode.values()),
      ...synthetic,
      // Pending multi-tooth selection — neutral violet wash that
      // distinguishes "in progress" from already-painted Tx items.
      { code: "__multi_tooth_pending", name: "Multi-tooth selection", color_hex: "#c4b5fd" },
    ];
  }, [findings, paletteCategories, procedures]);

  // Multi-tooth groups for the chart — one per non-declined plan item
  // whose `teeth` jsonb has 2+ entries. ToothChart paints a colored
  // frame + procedure label behind each group's contiguous teeth in
  // the same arch row, and the legend strip below the chart shows
  // only the kinds present on this plan (BRIDGE / DENTURE / RPD /
  // SPLINT / CROWN).
  //
  // Production architecture (20260609000001): server returns
  // `display_kind_label` directly on each item. We use it as the
  // primary source and fall back to client-side classifyMultiTooth
  // sniffing only when the server didn't compute one (legacy items
  // before the migration backfill, or pre-rollout payloads). The
  // kind→color map stays client-side because color is a presentation
  // concern.
  const multiToothGroups = useMemo(() => {
    const items = activePlan?.items || [];
    const out = [];
    for (const it of items) {
      if (it.status === "Declined") continue;
      // Preserve the per-tooth structure so role (abutment / pontic)
      // can be threaded into the chart for the A / P badge. Each entry:
      //   { tooth_number: "16", role: "abutment" | "pontic" | null }
      const teethArr = Array.isArray(it.teeth)
        ? it.teeth
            .map((t) => typeof t === "object"
              ? { tooth_number: t?.tooth_number, role: t?.role || null }
              : { tooth_number: t, role: null })
            .filter((t) => t.tooth_number)
        : [];
      if (teethArr.length < 2) continue;
      // Server-first; classifyMultiTooth as fallback. The server label
      // is "BRIDGE" / "SPLINT" / "RPD" / "DENTURE" / "CROWN" for known
      // kinds, NULL for unrecognized — we treat NULL as "fall back".
      const fallback = classifyMultiTooth({
        multi_tooth_kind: it.multi_tooth_kind,
        procedure_name:   it.procedure_name,
      });
      const label = it.display_kind_label || fallback.label;
      // Resolve color from the kind label (so the server label drives
      // both label + color consistently).
      const kind = it.display_kind_label
        ? (it.display_kind_label === "BRIDGE"  ? "bridge"
         : it.display_kind_label === "SPLINT"  ? "splint"
         : it.display_kind_label === "RPD"     ? "partial_denture"
         : it.display_kind_label === "DENTURE" ? "full_denture"
         : it.display_kind_label === "CROWN"   ? "crown_unit"
         : "other")
        : fallback.kind;
      const color = (kind === "bridge"          ? "#b91c1c"
                  : kind === "splint"          ? "#c2410c"
                  : kind === "partial_denture" ? "#047857"
                  : kind === "full_denture"    ? "#6d28d9"
                  : kind === "crown_unit"      ? "#0e7490"
                  : fallback.color);
      // Per-tooth role lookup, used by ToothChart to render A / P chips
      // inside each grouped cell.
      const rolesByFdi = {};
      for (const t of teethArr) {
        if (t.role) rolesByFdi[String(t.tooth_number)] = t.role;
      }
      out.push({
        id:    it.id,
        kind,
        label,
        color,
        teeth: teethArr.map((t) => String(t.tooth_number)),
        rolesByFdi,
      });
    }
    return out;
  }, [activePlan]);

  // Legend rows = one per *kind* present (collapse duplicates so a
  // plan with two Bridges shows BRIDGE once). Stable order so legend
  // doesn't reshuffle on item re-order.
  const multiToothLegend = useMemo(() => {
    const seen = new Map();
    for (const g of multiToothGroups) {
      if (!seen.has(g.kind)) seen.set(g.kind, { label: g.label, color: g.color });
    }
    return Array.from(seen.values());
  }, [multiToothGroups]);

  // ----- initial load -----
  const reload = useCallback(async () => {
    if (tutorialDemoMode) {
      applyTutorialDemoData();
      return;
    }
    setLoading(true); setError(null);
    const [plansRes, procRes, snapRes, findRes, paletteRes, settingsRes, mhRes, apptSettingsRes] = await Promise.all([
      supabase.rpc("list_treatment_plans", { p_patient_id: patientId }),
      supabase.rpc("list_clinic_procedures"),
      supabase.rpc("get_chart_current",    { p_patient_id: patientId }),
      supabase.rpc("list_chart_findings"),
      supabase.rpc("list_chart_palette"),
      supabase.rpc("get_clinic_settings"),
      supabase.rpc("get_medical_history",  { p_patient_id: patientId }),
      // Pulled separately from clinic settings so the Tx Plan can add
      // one setup overhead to each appointment/phase total.
      // get_clinic_settings doesn't include chair_setup_minutes today;
      // get_appointment_settings does.
      supabase.rpc("get_appointment_settings"),
    ]);
    setLoading(false);
    if (plansRes.error) { setError(plansRes.error.message ?? "Could not load plans."); return; }
    if (procRes.error)  { setError(procRes.error.message  ?? "Could not load procedures."); return; }
    if (snapRes.error)  { setError(snapRes.error.message  ?? "Could not load chart snapshot."); return; }
    if (findRes.error)  { setError(findRes.error.message  ?? "Could not load findings."); return; }
    if (paletteRes.error) { setError(paletteRes.error.message ?? "Could not load charting palette."); return; }
    const planList = treatmentPlanAuthoringPlans(plansRes.data);
    setPlans(planList);
    setProcedures(procRes.data ?? []);
    setSnapshot(snapRes.data ?? null);
    setFindings(findRes.data ?? []);
    setPaletteCategories(paletteRes.data?.categories ?? []);
    setMedicalHistory(mhRes.error ? null : (mhRes.data ?? null));
    if (!apptSettingsRes.error && apptSettingsRes.data?.chair_setup_minutes != null) {
      setChairSetupMinutes(Number(apptSettingsRes.data.chair_setup_minutes));
    }
    // Tolerate settings-load failure: PDFs still build without
    // letterhead, just fall back to the legacy "Dental Clinic" header.
    if (!settingsRes.error && settingsRes.data) {
      setClinicForPdf(await clinicSettingsWithLogo(settingsRes.data, activeClinic));
    }
    // Pick the most-recent draft if any, otherwise the most recent plan.
    // If the URL carries ?plan=<uuid> AND it's a real plan for this patient
    // (Tx Plan Center deep-link), honor that selection instead.
    const urlPlanId = deepLinkPlanIdRef.current;
    const fromUrl = urlPlanId ? planList.find((p) => p.id === urlPlanId) : null;
    const next = fromUrl
      ?? planList.find((p) => p.status === "Draft")
      ?? planList[0];
    setActivePlanId(next?.id ?? null);
  }, [patientId, activeClinic, tutorialDemoMode, applyTutorialDemoData]);

  useEffect(() => {
    if (tutorialDemoMode) return;
    reload();
  }, [reload, tutorialDemoMode]);

  // Wake the Vercel Python serverless function while the user is
  // browsing the plan — by the time they click Send, the function
  // is warm and the email send drops from ~30s to ~2-3s. Fire-and-
  // forget; errors swallowed inside the helper.
  useEffect(() => {
    if (tutorialDemoMode) return;
    prewarmEmailBackend();
  }, [tutorialDemoMode]);

  // ----- load active plan whenever the active id changes -----
  useEffect(() => {
    if (tutorialDemoMode) return;
    if (!activePlanId) { setActivePlan(null); return; }
    let cancelled = false;
    supabase.rpc("get_treatment_plan", { p_plan_id: activePlanId }).then(({ data, error: e }) => {
      if (cancelled) return;
      if (e) { toast.error(e.message ?? "Could not load plan."); setActivePlan(null); return; }
      const nextPlan = data ?? null;
      setActivePlan(nextPlan);
      onActivePlanDataChange?.(nextPlan);
    });
    return () => { cancelled = true; };
  }, [activePlanId, tutorialDemoMode, onActivePlanDataChange]);

  const refreshSignatureDocument = useCallback(async (planId = activePlanId) => {
    if (tutorialDemoMode || !planId) {
      setSignatureDocument(null);
      return null;
    }
    try {
      const doc = await getTreatmentPlanDocumentStatus({ planId });
      setSignatureDocument(doc || null);
      return doc || null;
    } catch (err) {
      console.warn("[treatment] signature status failed", err);
      setSignatureDocument(null);
      return null;
    }
  }, [activePlanId, tutorialDemoMode]);

  useEffect(() => {
    refreshSignatureDocument(activePlanId);
  }, [activePlanId, refreshSignatureDocument]);

  const refreshActive = useCallback(async () => {
    if (tutorialDemoMode) {
      applyTutorialDemoData();
      return;
    }
    if (!activePlanId) return;
    const { data, error: e } = await supabase.rpc("get_treatment_plan", { p_plan_id: activePlanId });
    if (!e) {
      const nextPlan = data ?? null;
      setActivePlan(nextPlan);
      onActivePlanDataChange?.(nextPlan);
    }
    // also refresh the plan-list (counts/status changed).
    const { data: list } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
    setPlans(treatmentPlanAuthoringPlans(list));
    // Phase 5: refresh plan balance for the close + collect dialogs.
    const { data: bal } = await supabase.rpc("tx_plan_balance", { p_plan_id: activePlanId });
    setPlanBalance(bal ?? null);
    await refreshSignatureDocument(activePlanId);
  }, [activePlanId, patientId, tutorialDemoMode, applyTutorialDemoData, onActivePlanDataChange, refreshSignatureDocument]);

  // Embed contract — let the parent (Doctor view) trigger a full
  // refresh after it writes through its own paint path so the
  // builder + plan tabs stay in lockstep with the chairside chart.
  // Held in a ref so the imperative handle can expose createPlan without
  // listing the (later-defined) callback in its dep array.
  const onCreatePlanRef = useRef(null);
  useImperativeHandle(ref, () => ({
    refresh: async () => {
      if (tutorialDemoMode) {
        applyTutorialDemoData();
        return;
      }
      const { data: list } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
      const rows = treatmentPlanAuthoringPlans(list);
      setPlans(rows);
      // If the active plan was just superseded (e.g. parent
      // auto-revised), follow into the new Draft so the user
      // doesn't see a stale Plan #N.
      const cur = rows.find((p) => p.id === activePlanId);
      if (cur?.status === "Superseded" && cur.superseded_by_id) {
        setActivePlanId(cur.superseded_by_id);
      } else {
        await refreshActive();
      }
    },
    setActivePlanId,
    // Doctor view's procedures palette has its own Multi-Tooth button —
    // it triggers the BuildMultiToothDialog that lives inside this
    // TreatmentTab (chartless mount). Exposed here so Doctor.jsx can
    // open the dialog without owning its state.
    openMultiTooth: () => {
      if (tutorialDemoMode) return;
      setMultiToothOpen(true);
    },
    // Lets an embedder that renders its own plan-tabs selector (Doctor Tx
    // Plan gapSlot) trigger the same create flow this tab owns.
    createPlan: () => onCreatePlanRef.current?.(),
  }), [patientId, activePlanId, refreshActive, tutorialDemoMode, applyTutorialDemoData]);

  // Notify the embedder of plan-tab selection changes so its own
  // paint/overlay state can follow the user's choice.
  useEffect(() => {
    onActivePlanIdChange?.(activePlanId);
  }, [activePlanId, onActivePlanIdChange]);

  // ----- create new plan -----
  const onCreatePlan = useCallback(async () => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, create_plan: true }));
    const { data, error: e } = await supabase.rpc("create_treatment_plan", {
      p_patient_id: patientId, p_doctor_id: null, p_notes: null,
    });
    setBusy((b) => ({ ...b, create_plan: false }));
    if (e) { toast.error(e.message ?? "Could not create plan."); return; }
    toast.success("New draft plan started.");
    setActivePlanId(data.id);
    // refresh plan list
    const { data: list } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
    setPlans(treatmentPlanAuthoringPlans(list));
  }, [patientId, blockTutorialDemoMutation]);
  onCreatePlanRef.current = onCreatePlan;

  // Mirror the plan-tab state up to an embedder that renders its own
  // selector (Doctor Tx Plan gapSlot). This tab stays the single source
  // of truth; the external selector is a controlled view of these values.
  useEffect(() => {
    onPlansChange?.({ plans, activePlanId, busyCreate: !!busy.create_plan, canCreate: canEdit });
  }, [plans, activePlanId, busy.create_plan, canEdit, onPlansChange]);

  // ----- item actions (declared first; the chart-paint handlers below
  // call onDeleteItem when the user paints over an item with the eraser) -----
  // Capture a plan-item snapshot before deleting so undo can re-create it.
  // After re-create the new id is captured in idHolder so a subsequent
  // redo can target the (newly-minted) row.
  const onDeleteItem = useCallback(async (item_id) => {
    if (blockTutorialDemoMutation()) return;
    const planId = activePlanId;
    const snap   = activePlan?.items?.find((i) => i.id === item_id);
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("delete_plan_item", { p_item_id: item_id });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not remove item."); return; }
    if (snap && planId) {
      const idHolder = { current: item_id };       // captured by closure
      const restoreInput = {
        procedure_id: snap.procedure_id,
        tooth_number: snap.tooth_number,
        surface:      snap.surface,
      };
      recordOp({
        label: "Delete item",
        undo: async () => {
          const { data, error } = await supabase.rpc("add_plan_item", {
            p_plan_id: planId, p_input: restoreInput,
          });
          if (error) throw new Error(error.message ?? "Could not restore item.");
          if (data?.id) idHolder.current = data.id;
        },
        redo: async () => {
          const { error } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
          if (error) throw new Error(error.message ?? "Could not delete item.");
        },
      });
    }
    await refreshActive();
  }, [refreshActive, activePlan, activePlanId, recordOp, blockTutorialDemoMutation]);

  // ----- chart paint handlers -----
  const onApplyToSurface = useCallback(async (fdi, surface) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan || activePlan.status !== "Draft") return;
    if (!armed) return;
    // Multi-Tooth mode: tooth taps toggle the buffer (whole-tooth,
    // surface ignored). Adjacency enforced client-side with a toast;
    // server's _fdi_are_adjacent re-validates on add_procedure_to_plan.
    if (armed.kind === "multi_tooth") {
      const fdiStr = String(fdi);
      setMultiToothBuffer((prev) => {
        const isRemoving = prev.includes(fdiStr);
        const next = isRemoving
          ? prev.filter((x) => x !== fdiStr)
          : [...prev, fdiStr];
        if (!isRemoving && !teethAreAdjacent(next)) {
          toast.error("Adjacent teeth only — same arch, no gaps.");
          return prev;
        }
        return next;
      });
      return;
    }
    if (armed.kind === "erase") {
      // Try exact (tooth, surface) match first. If none, fall back to
      // a whole-tooth item on this tooth (surface=null) so clicking any
      // surface erases procedures painted as whole-tooth too.
      const items = activePlan.items ?? [];
      const target = items.find(
        (i) => String(i.tooth_number) === String(fdi) && i.surface === surface,
      ) || items.find(
        (i) => String(i.tooth_number) === String(fdi) && i.surface == null,
      );
      if (target) await onDeleteItem(target.id);
      return;
    }
    if (armed.kind === "procedure") {
      const planId = activePlan.id;
      // Whole-tooth procedures (Crown, Veneer, Bridge, Extraction, RCT,
      // Implant, etc.) cover the entire tooth — there's no clinical
      // sense to splitting them by surface. Force surface=null when
      // the armed procedure's category lives in WHOLE_TOOTH_CATEGORIES,
      // regardless of which surface the user clicked.
      const armedProc   = procedures.find((p) => p.id === armed.id);
      const wholeTooth  = isWholeToothProcedure(armedProc);
      const effSurface  = wholeTooth ? null : surface;
      const payload = {
        procedure_id: armed.id,
        tooth_number: String(fdi),
        surface: effSurface,
      };
      const { data, error: e } = await supabase.rpc("add_plan_item", {
        p_plan_id: planId, p_input: payload,
      });
      if (e) { toast.error(e.message ?? "Could not add item."); return; }
      if (data?.id) {
        const idHolder = { current: data.id };
        recordOp({
          label: "Add item",
          undo: async () => {
            const { error } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
            if (error) throw new Error(error.message ?? "Could not undo add.");
          },
          redo: async () => {
            const { data: re, error } = await supabase.rpc("add_plan_item", {
              p_plan_id: planId, p_input: payload,
            });
            if (error) throw new Error(error.message ?? "Could not redo add.");
            if (re?.id) idHolder.current = re.id;
          },
        });
      }
      await refreshActive();
    }
  }, [activePlan, armed, procedures, refreshActive, onDeleteItem, recordOp, blockTutorialDemoMutation]);

  const onPickWholeMouth = useCallback(async (proc) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan || activePlan.status !== "Draft" || !proc) return;
    const alreadyInPlan = (activePlan.items ?? []).find((it) =>
      it.procedure_id === proc.id
      && (it.tooth_number ?? null) == null
      && (it.surface ?? null) == null
      && it.status !== "Declined");
    if (alreadyInPlan) {
      toast.info(`${proc.name} is already in this plan.`);
      return;
    }
    const planId = activePlan.id;
    const payload = {
      procedure_id: proc.id,
      tooth_number: null,
      surface: null,
    };
    const { data, error: e } = await supabase.rpc("add_plan_item", {
      p_plan_id: planId,
      p_input: payload,
    });
    if (e) {
      toast.error(e.message ?? "Could not add item.");
      return;
    }
    if (data?.id) {
      const idHolder = { current: data.id };
      recordOp({
        label: "Add whole-mouth item",
        undo: async () => {
          const { error } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
          if (error) throw new Error(error.message ?? "Could not undo add.");
        },
        redo: async () => {
          const { data: re, error } = await supabase.rpc("add_plan_item", {
            p_plan_id: planId,
            p_input: payload,
          });
          if (error) throw new Error(error.message ?? "Could not redo add.");
          if (re?.id) idHolder.current = re.id;
        },
      });
    }
    setArmed(null);
    setMultiToothBuffer([]);
    toast.success(`${proc.name} added as whole mouth.`);
    await refreshActive();
  }, [activePlan, refreshActive, recordOp, blockTutorialDemoMutation]);

  const onCreateProcedureAndAddToPlan = useCallback(async (draft) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan || activePlan.status !== "Draft") {
      toast.error("Start or select a draft plan before adding a service.");
      return;
    }
    const feeCentavos = Math.round(Number(draft.feePesos || 0) * 100);
    setBusy((b) => ({ ...b, add_procedure_to_plan: true }));
    const { data: created, error: createError } = await supabase.rpc("create_procedure_from_plan", {
      p_plan_id: activePlan.id,
      p_input: {
        name: draft.name.trim(),
        short_code: draft.shortCode.trim() || null,
        code: null,
        category: draft.category,
        default_fee_centavos: feeCentavos,
        suggested_duration_minutes: draft.duration === "" ? null : Number(draft.duration),
        unit_basis: "fixed",
        uses_chair_setup: true,
        multi_visit: false,
        default_booking_window_days: null,
        tooth_specific: draft.chartPlacement !== "whole_mouth",
        surface_specific: draft.chartPlacement === "surface",
        additional_surface_pct: null,
        is_extraction: false,
        clears_calculus_deposits: false,
        tooth_count: draft.chartPlacement === "whole_mouth" ? "none" : "single",
        multi_tooth_kind: null,
        tooth_role_required: false,
        requires_lab_work: false,
        ortho_visit_type: null,
        visit_count: 1,
        default_visit_template: [],
        notes: "Added from the treatment plan builder.",
      },
    });
    if (createError) {
      setBusy((b) => ({ ...b, add_procedure_to_plan: false }));
      toast.error(createError.message ?? "Could not add service.");
      throw createError;
    }

    const procedure = Array.isArray(created) ? created[0] : created;
    if (!procedure?.id) {
      setBusy((b) => ({ ...b, add_procedure_to_plan: false }));
      const err = new Error("The service was created but the database did not return its ID.");
      toast.error(err.message);
      throw err;
    }

    const payload = {
      procedure_id: procedure.id,
      tooth_number: null,
      surface: null,
    };
    const { data: added, error: addError } = await supabase.rpc("add_plan_item", {
      p_plan_id: activePlan.id,
      p_input: payload,
    });
    if (addError) {
      setBusy((b) => ({ ...b, add_procedure_to_plan: false }));
      toast.error(addError.message ?? "Service was created, but could not be added to the plan.");
      throw addError;
    }

    if (added?.id) {
      const idHolder = { current: added.id };
      recordOp({
        label: "Add new service to plan",
        undo: async () => {
          const { error } = await supabase.rpc("delete_plan_item", { p_item_id: idHolder.current });
          if (error) throw new Error(error.message ?? "Could not undo add.");
        },
        redo: async () => {
          const { data, error } = await supabase.rpc("add_plan_item", {
            p_plan_id: activePlan.id,
            p_input: payload,
          });
          if (error) throw new Error(error.message ?? "Could not redo add.");
          if (data?.id) idHolder.current = data.id;
        },
      });
    }

    const { data: nextProcedures } = await supabase.rpc("list_clinic_procedures");
    setProcedures(nextProcedures ?? []);
    await refreshActive();
    setBusy((b) => ({ ...b, add_procedure_to_plan: false }));
    setAddProcedureOpen(null);
    toast.success(`${procedure.name} added to services and this plan.`);
  }, [activePlan, blockTutorialDemoMutation, recordOp, refreshActive]);

  const onClearTooth = useCallback(async (fdi) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan || activePlan.status !== "Draft") return;
    if (armed?.kind !== "erase") return;
    const targets = (activePlan.items ?? []).filter((i) => String(i.tooth_number) === String(fdi));
    for (const t of targets) await onDeleteItem(t.id);
  }, [activePlan, armed, onDeleteItem, blockTutorialDemoMutation]);

  const onSetItemStatus = useCallback(async (item_id, status, reason) => {
    if (blockTutorialDemoMutation()) return;
    const prev = activePlan?.items?.find((i) => i.id === item_id);
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("set_plan_item_status", {
      p_item_id: item_id, p_status: status, p_reason: reason ?? null,
    });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not update item."); return; }
    toast.success(`Item ${status.toLowerCase()}.`);
    if (prev) {
      recordOp({
        label: `Set status → ${prev.status}`,
        undo: () => supabase.rpc("set_plan_item_status", { p_item_id: item_id, p_status: prev.status, p_reason: prev.declined_reason ?? null }),
        redo: () => supabase.rpc("set_plan_item_status", { p_item_id: item_id, p_status: status,        p_reason: reason ?? null }),
      });
    }
    await refreshActive();
  }, [refreshActive, activePlan, recordOp, blockTutorialDemoMutation]);

  // Per-line discount — works on ANY plan status. set_plan_item_discount
  // mutates only the discount triplet so non-Draft plans keep their
  // fee/procedure/surface frozen.
  const onSetItemDiscount = useCallback(async (item_id, { pct, amount, reason }) => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("set_plan_item_discount", {
      p_item_id: item_id,
      p_pct:     pct ?? null,
      p_amount:  amount ?? null,
      p_reason:  reason ?? null,
    });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not update discount."); return; }
    await refreshActive();
  }, [refreshActive, blockTutorialDemoMutation]);

  // Per-line price + note — Draft only (server-enforced).
  const onSetItemPrice = useCallback(async (item_id, centavos) => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("update_plan_item", {
      p_item_id: item_id,
      p_changes: { fee_centavos: Number(centavos || 0) },
    });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not update price."); return; }
    await refreshActive();
  }, [refreshActive, blockTutorialDemoMutation]);

  const onSetItemNote = useCallback(async (item_id, notes) => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("update_plan_item", {
      p_item_id: item_id,
      p_changes: { notes: notes ?? "" },
    });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not update note."); return; }
    await refreshActive();
  }, [refreshActive, blockTutorialDemoMutation]);

  const onSetItemPhase = useCallback(async (item_id, phase_number) => {
    if (blockTutorialDemoMutation()) return;
    const prev = activePlan?.items?.find((i) => i.id === item_id);
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("set_plan_item_phase", {
      p_item_id: item_id, p_phase_number: phase_number == null ? 0 : phase_number,
    });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not change phase."); return; }
    if (prev) {
      const prevP = prev.phase_number == null ? 0 : prev.phase_number;
      const newP  = phase_number == null ? 0 : phase_number;
      recordOp({
        label: `Item phase ${newP} ← ${prevP}`,
        undo: () => supabase.rpc("set_plan_item_phase", { p_item_id: item_id, p_phase_number: prevP }),
        redo: () => supabase.rpc("set_plan_item_phase", { p_item_id: item_id, p_phase_number: newP }),
      });
    }
    await refreshActive();
  }, [refreshActive, activePlan, recordOp, blockTutorialDemoMutation]);

  const onSetToothPhase = useCallback(async (tooth_number, phase_number) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan) return;
    const targets = (activePlan.items ?? []).filter(
      (i) => String(i.tooth_number) === String(tooth_number),
    );
    if (targets.length === 0) return;
    const phase = phase_number == null ? 0 : phase_number;
    // Snapshot prev phase for each item BEFORE we mutate, so undo
    // restores per-item state (handles "mixed" teeth correctly).
    const prevByItem = targets.map((t) => ({ id: t.id, prev: t.phase_number == null ? 0 : t.phase_number }));
    // Loop in series so failures (RLS / permissions) surface predictably.
    for (const t of targets) {
      const { error: e } = await supabase.rpc("set_plan_item_phase", {
        p_item_id: t.id, p_phase_number: phase,
      });
      if (e) { toast.error(e.message ?? "Could not change phase."); break; }
    }
    recordOp({
      label: `Tooth ${tooth_number} → phase ${phase}`,
      undo: async () => { for (const x of prevByItem) await supabase.rpc("set_plan_item_phase", { p_item_id: x.id, p_phase_number: x.prev }); },
      redo: async () => { for (const x of prevByItem) await supabase.rpc("set_plan_item_phase", { p_item_id: x.id, p_phase_number: phase }); },
    });
    await refreshActive();
  }, [activePlan, refreshActive, recordOp, blockTutorialDemoMutation]);

  const onRenamePhase = useCallback(async (phase_number, name) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId) return;
    const prev = activePlan?.phase_names?.[String(phase_number)] ?? null;
    const next = name || null;
    const { error: e } = await supabase.rpc("rename_plan_phase", {
      p_plan_id: activePlanId, p_phase_number: phase_number, p_name: next,
    });
    if (e) { toast.error(e.message ?? "Could not rename phase."); return; }
    recordOp({
      label: `Phase ${phase_number} rename`,
      undo: () => supabase.rpc("rename_plan_phase", { p_plan_id: activePlanId, p_phase_number: phase_number, p_name: prev }),
      redo: () => supabase.rpc("rename_plan_phase", { p_plan_id: activePlanId, p_phase_number: phase_number, p_name: next }),
    });
    await refreshActive();
  }, [activePlanId, activePlan, recordOp, refreshActive, blockTutorialDemoMutation]);

  // "Add phase" materialises a phase server-side with a default label
  // so the tab is visible immediately even when no items are assigned.
  // Default is "Appointment N" — phase = appointment is the working
  // mental model. Special case: when adding the FIRST phase (going from
  // 0 phases to phased), seed Phase 1 AND Phase 2 in one click — phasing
  // a plan implies at least two appointments, so making the user click
  // twice is friction.
  const onAddPhase = useCallback(async (phase_number) => {
    if (tutorialDemoMode && tutorialDemoData?.handlers?.onAddPhase) {
      await tutorialDemoData.handlers.onAddPhase(phase_number);
      return;
    }
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId || !activePlan) return;
    const items = activePlan.items ?? [];
    const phaseNames = activePlan.phase_names || {};
    const hadAnyPhase = items.some((i) => Number(i.phase_number) > 0)
      || Object.keys(phaseNames).length > 0;
    const seedPair = !hadAnyPhase && phase_number === 1;
    if (seedPair) {
      const { error: e } = await supabase.rpc("initialize_plan_appointments_unassigned", {
        p_plan_id: activePlanId,
      });
      if (e) { toast.error(e.message ?? "Could not add appointments."); return; }
      await refreshActive();
      return;
    }
    const { error: e } = await supabase.rpc("rename_plan_phase", {
      p_plan_id: activePlanId,
      p_phase_number: phase_number,
      p_name: `Appointment ${phase_number}`,
    });
    if (e) { toast.error(e.message ?? "Could not add phase."); return; }
    await refreshActive();
  }, [activePlanId, activePlan, refreshActive, blockTutorialDemoMutation, tutorialDemoMode, tutorialDemoData]);

  // Schedule the whole phase to a single appointment. Only items that
  // are eligible for scheduling (Accepted / already Scheduled) are
  // sent to the dialog; the rest are filtered out so the user isn't
  // confused when half the items "fail".
  const onSchedulePhase = useCallback((phase_number) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlan) return;
    const items = (activePlan.items ?? []).filter(
      (i) => Number(i.phase_number) === phase_number
        && (i.status === "Accepted" || i.status === "Scheduled"),
    );
    if (items.length === 0) {
      toast.warning("No Accepted items in this phase to schedule. Accept items first.");
      return;
    }
    const phaseLabel = activePlan.phase_names?.[String(phase_number)] || `Appointment ${phase_number}`;
    setSchedulePhase({
      items,
      phaseNumber: phase_number,
      title: `${phaseLabel} · ${items.length} item${items.length === 1 ? "" : "s"}`,
    });
  }, [activePlan, blockTutorialDemoMutation]);

  const onSetPhaseTiming = useCallback(async (phase_number, offset_days, after_phase) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId) return;
    const prev = activePlan?.phase_meta?.[String(phase_number)] || null;
    const cleanOffset = offset_days == null ? 0 : offset_days;
    const cleanAfter  = after_phase  == null ? 0 : after_phase;
    const { error: e } = await supabase.rpc("set_phase_timing", {
      p_plan_id: activePlanId,
      p_phase_number: phase_number,
      p_offset_days:  cleanOffset,
      p_after_phase:  cleanAfter,
    });
    if (e) { toast.error(e.message ?? "Could not set phase timing."); return; }
    // Record undo. Pass 0 to clear; the prev meta object restores the
    // earlier timing (or clears it if there was none).
    recordOp({
      label: `Phase ${phase_number} timing`,
      undo: () => supabase.rpc("set_phase_timing", {
        p_plan_id: activePlanId,
        p_phase_number: phase_number,
        p_offset_days:  prev?.offset_days ?? 0,
        p_after_phase:  prev?.after_phase ?? 0,
      }),
      redo: () => supabase.rpc("set_phase_timing", {
        p_plan_id: activePlanId,
        p_phase_number: phase_number,
        p_offset_days:  cleanOffset,
        p_after_phase:  cleanAfter,
      }),
    });
    await refreshActive();
  }, [activePlanId, activePlan, recordOp, refreshActive, blockTutorialDemoMutation]);

  // Per-tooth notes — text annotation that surfaces under the tooth in
  // the plan list, on the chart's note pill, and on the patient PDF.
  // Throws on RPC failure so callers (notably ToothNoteDialog) can
  // keep the modal open and show the error inline instead of
  // dismissing it and losing the user's draft.
  const onSetToothNote = useCallback(async (tooth_number, text) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId) return;
    const prev = activePlan?.tooth_notes?.[String(tooth_number)] ?? null;
    const next = (text == null || text.trim() === "") ? null : text;
    const { error: e } = await supabase.rpc("set_plan_tooth_note", {
      p_plan_id:      activePlanId,
      p_tooth_number: String(tooth_number),
      p_note:         next,
    });
    if (e) {
      toast.error(e.message ?? "Could not save note.");
      throw new Error(e.message ?? "Could not save note.");
    }
    recordOp({
      label: `Tooth ${tooth_number} note`,
      undo: () => supabase.rpc("set_plan_tooth_note", {
        p_plan_id: activePlanId, p_tooth_number: String(tooth_number), p_note: prev,
      }),
      redo: () => supabase.rpc("set_plan_tooth_note", {
        p_plan_id: activePlanId, p_tooth_number: String(tooth_number), p_note: next,
      }),
    });
    await refreshActive();
  }, [activePlanId, activePlan, recordOp, refreshActive, blockTutorialDemoMutation]);

  // Delete phase: clear the assignment from every item in this phase
  // (set phase_number → null), then clear the custom name. The phase
  // number itself isn't reused — gaps are fine, dentists rarely care.
  const onDeletePhase = useCallback(async (phase_number) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId || !activePlan) return;
    const stuck = (activePlan.items ?? []).filter((i) => Number(i.phase_number) === phase_number);
    for (const it of stuck) {
      const { error: e } = await supabase.rpc("set_plan_item_phase", {
        p_item_id: it.id, p_phase_number: 0,
      });
      if (e) { toast.error(e.message ?? "Could not delete phase."); return; }
    }
    const { error: e2 } = await supabase.rpc("rename_plan_phase", {
      p_plan_id: activePlanId, p_phase_number: phase_number, p_name: null,
    });
    if (e2) { toast.error(e2.message ?? "Could not clear phase label."); return; }
    toast.success("Phase deleted.");
    await refreshActive();
  }, [activePlan, activePlanId, refreshActive, blockTutorialDemoMutation]);

  const onComparePlans = useCallback(async (otherPlanId) => {
    if (blockTutorialDemoMutation()) return;
    if (!activePlanId) return;
    setPdfDoctorError(null);
    // Re-fetch BOTH plans so the PDF reflects what's actually saved.
    const [aRes, bRes] = await Promise.all([
      supabase.rpc("get_treatment_plan", { p_plan_id: activePlanId }),
      supabase.rpc("get_treatment_plan", { p_plan_id: otherPlanId }),
    ]);
    if (aRes.error || bRes.error) {
      toast.error((aRes.error || bRes.error).message ?? "Could not load plans.");
      return;
    }
    const blockA = checkUnassignedBlock(aRes.data);
    const blockB = checkUnassignedBlock(bRes.data);
    if (blockA || blockB) {
      toast.error(blockA ? `This plan: ${blockA}` : `Other plan: ${blockB}`);
      return;
    }
    // Generation gate: both plans must have a real doctor display name.
    // The patient-facing PDF is never allowed to fall back to placeholders.
    const gateA = treatmentPlanDoctorGate(aRes.data);
    const gateB = treatmentPlanDoctorGate(bRes.data);
    if (!gateA.ok || !gateB.ok) {
      const gate = !gateA.ok ? gateA : gateB;
      setPdfDoctorError(gate);
      toast.error(gate.message);
      return;
    }
    try {
      const doc = await buildTreatmentPlanPdf({
        plans: [aRes.data, bRes.data],
        patient,
        clinic: clinicForPdf || activeClinic,
        doctorName: aRes.data?.doctor_name,
        doctorPrcLicense: aRes.data?.doctor_prc_license,
      });
      doc.save(pdfFilename(patient, "comparison"));
    } catch (err) {
      toast.error(err?.message || "Could not generate comparison PDF.");
      console.warn("[treatment] compare PDF failed", err);
    }
  }, [activePlanId, patient, activeClinic, clinicForPdf, blockTutorialDemoMutation]);

  const onDownloadPdf = useCallback(async (plan_id) => {
    if (blockTutorialDemoMutation()) return;
    setPdfDoctorError(null);
    // Always re-fetch so the PDF reflects the saved plan, even if
    // local state is mid-edit.
    const { data: planData, error: e } = await supabase.rpc("get_treatment_plan", { p_plan_id: plan_id });
    if (e) { toast.error(e.message ?? "Could not load plan."); return; }
    const block = checkUnassignedBlock(planData);
    if (block) { toast.error(block); return; }
    // Generation gate: doctor display name must be real. Placeholders
    // such as "Owner" are not allowed on patient-facing PDFs.
    const gate = treatmentPlanDoctorGate(planData);
    if (!gate.ok) {
      setPdfDoctorError(gate);
      toast.error(gate.message);
      return;
    }
    try {
      const docStatus = await refreshSignatureDocument(plan_id);
      // Anchor the PDF's per-visit times to the same appointment durations
      // the board shows (booked → typed → chair + setup), so they line up.
      const procUsesSetup = new Map((procedures || []).map((p) => [p.id, p.uses_chair_setup !== false]));
      const planForPdf = {
        ...planData,
        phase_durations: computePhaseDurations(
          planData?.items || [],
          planData?.phase_meta || {},
          { chairSetupMinutes, procedureUsesSetup: procUsesSetup },
        ),
      };
      const doc = await buildTreatmentPlanPdf({
        plan: planForPdf,
        patient,
        clinic: clinicForPdf || activeClinic,
        doctorName: planData?.doctor_name,
        doctorPrcLicense: planData?.doctor_prc_license,
        signature: signatureForPdf(docStatus),
      });
      doc.save(pdfFilename(patient));
    } catch (err) {
      toast.error(err?.message || "Could not generate PDF.");
      console.warn("[treatment] PDF download failed", err);
    }
  }, [patient, activeClinic, clinicForPdf, procedures, chairSetupMinutes, blockTutorialDemoMutation, refreshSignatureDocument]);

  const onOpenSignDocument = useCallback((plan) => {
    if (!plan?.id) return;
    setSigningPlan(plan);
  }, []);

  const onSubmitSignature = useCallback(async (signature) => {
    if (!signingPlan?.id) return;
    setSignatureBusy(true);
    try {
      const acceptPlan = signingPlan.status === "Proposed";
      const result = await signTreatmentPlanDocument({
        planId: signingPlan.id,
        signature,
        acceptPlan,
      });
      setSignatureDocument(result || null);
      setSigningPlan(null);
      toast.success(acceptPlan ? "Plan signed and accepted." : "Document signed.");
      await refreshActive();
    } catch (err) {
      toast.error(err?.message || "Could not save signature.");
      console.warn("[treatment] signature save failed", err);
    } finally {
      setSignatureBusy(false);
    }
  }, [refreshActive, signingPlan]);

  const onUnscheduleItem = useCallback(async (item_id) => {
    if (blockTutorialDemoMutation()) return;
    const prev = activePlan?.items?.find((i) => i.id === item_id);
    const prevApptId = prev?.scheduled_appointment_id ?? null;
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: true } }));
    const { error: e } = await supabase.rpc("unschedule_plan_item", { p_item_id: item_id });
    setBusy((b) => ({ ...b, item: { ...(b.item || {}), [item_id]: false } }));
    if (e) { toast.error(e.message ?? "Could not unschedule."); return; }
    toast.success("Item unscheduled.");
    if (prevApptId) {
      recordOp({
        label: "Unschedule item",
        undo: () => supabase.rpc("schedule_plan_item",   { p_item_id: item_id, p_appointment_id: prevApptId }),
        redo: () => supabase.rpc("unschedule_plan_item", { p_item_id: item_id }),
      });
    }
    await refreshActive();
  }, [refreshActive, activePlan, recordOp, blockTutorialDemoMutation]);

  // Shared helper: build the PDF, send it via the email relay, and
  // surface friendly toasts for the three outcomes (no email on file,
  // domain not verified, generic failure). Used by both the initial
  // propose flow and the "Resend email" button on Proposed plans.
  // `successMessage` lets the caller customize the success toast.
  const emailPlan = useCallback(async (planData, { successMessage }) => {
    if (blockTutorialDemoMutation()) return null;
    return emailTreatmentPlanPdf({
      planData,
      patient,
      clinicForPdf,
      activeClinic,
      successMessage,
      toast,
    });
  }, [patient, activeClinic, clinicForPdf, blockTutorialDemoMutation]);

  const onPropose = useCallback(async (plan_id) => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, propose: true }));
    const { error: rpcErr } = await supabase.rpc("propose_treatment_plan", { p_plan_id: plan_id });
    if (rpcErr) {
      setBusy((b) => ({ ...b, propose: false }));
      toast.error(rpcErr.message ?? "Could not propose plan.");
      return;
    }
    const { data: planData } = await supabase.rpc("get_treatment_plan", { p_plan_id: plan_id });
    await emailPlan(planData ?? activePlan, { successMessage: "Plan proposed and emailed to patient." });
    setBusy((b) => ({ ...b, propose: false }));
    await refreshActive();
  }, [refreshActive, activePlan, emailPlan, blockTutorialDemoMutation]);

  // Resend button on Proposed plans. Does NOT change the plan's state —
  // just re-fires the email + PDF. Same unassigned-block guard so the
  // resent email never carries an "Unassigned" section.
  const onResendEmail = useCallback(async (plan_id) => {
    if (blockTutorialDemoMutation()) return;
    setBusy((b) => ({ ...b, resend: true }));
    const { data: planData, error: e } = await supabase.rpc("get_treatment_plan", { p_plan_id: plan_id });
    if (e) {
      setBusy((b) => ({ ...b, resend: false }));
      toast.error(e.message ?? "Could not load plan.");
      return;
    }
    const block = checkUnassignedBlock(planData);
    if (block) {
      setBusy((b) => ({ ...b, resend: false }));
      toast.error(block);
      return;
    }
    await emailPlan(planData, { successMessage: "Plan resent to patient." });
    setBusy((b) => ({ ...b, resend: false }));
  }, [emailPlan, blockTutorialDemoMutation]);

  const onRevise = useCallback(async (plan_id) => {
    if (blockTutorialDemoMutation()) return;
    const { data, error: e } = await supabase.rpc("revise_treatment_plan", { p_plan_id: plan_id });
    if (e) { toast.error(e.message ?? "Could not revise plan."); return; }
    toast.success("Plan revised — new draft created.");
    setActivePlanId(data.new_plan_id);
    const { data: list } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
    setPlans(treatmentPlanAuthoringPlans(list));
  }, [patientId, blockTutorialDemoMutation]);

  const onDeleteDraftPlan = useCallback(async (plan_id) => {
    if (blockTutorialDemoMutation()) return;
    if (!window.confirm("Discard this draft? This cannot be undone.")) return;
    const { error: e } = await supabase.rpc("delete_draft_plan", { p_plan_id: plan_id });
    if (e) { toast.error(e.message ?? "Could not delete draft."); return; }
    toast.success("Draft discarded.");
    setActivePlanId(null);
    const { data: list } = await supabase.rpc("list_treatment_plans", { p_patient_id: patientId });
    const rows = treatmentPlanAuthoringPlans(list);
    setPlans(rows);
    const next = rows.find((p) => p.status === "Draft") ?? rows[0];
    setActivePlanId(next?.id ?? null);
  }, [patientId, blockTutorialDemoMutation]);

  // ----- render -----
  if (loading) {
    return <div data-testid="treatment-tab-loading" className="text-sm text-slate-400 px-2">Loading treatment…</div>;
  }
  if (error) {
    return <div data-testid="treatment-tab-error" className="bg-rose-50 border border-rose-200 rounded-xl p-6 text-sm text-rose-700">{error}</div>;
  }

  const isDraft     = activePlan?.status === "Draft";
  const armedActive = armed != null;
  const planTabsCard = (
    <div className={`bg-white rounded-2xl shadow-sm p-4 sm:p-5 ${chartless ? "max-w-4xl" : ""}`}>
      <PlanTabsRow
        plans={plans}
        activePlanId={activePlanId}
        busyNew={busy.create_plan}
        canCreate={canEdit}
        onPick={setActivePlanId}
        onCreate={onCreatePlan}
      />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="treatment-tab">
      {/* Medical-history liability banner — render above plans so the
          dentist sees it before they touch the chart. Hidden in
          chartless mode because the chairside workspace already
          shows an MH banner above the chart. */}
      {!chartless && !chartFirst ? <MedicalHistoryBanner mh={medicalHistory} /> : null}

      {/* Plan tabs row — hidden when the embedder renders its own copy
          (Doctor Tx Plan tucks the selector under the chart via gapSlot). */}
      {(chartFirst || hidePlanTabs) ? null : planTabsCard}

      {/* Chart + palette + builder — when the chart is shown, the
          builder lives in the left column right below the chart card
          so the palette's right rail (which is taller than the chart
          alone) doesn't leave dead space on the left. Mirrors the
          chairside Doctor.jsx `belowChartCanvas` layout. In chartless
          mode (Doctor view's chairside Tx Plan) the chart row is
          hidden and the builder renders standalone below. */}
      {chartless ? null : (
      <div className={`grid gap-4 ${isDraft ? "xl:grid-cols-[minmax(0,1fr)_240px]" : "xl:grid-cols-1"}`}>
        <div className="flex flex-col gap-4 min-w-0">
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 overflow-x-auto min-w-0" data-testid="treatment-tooth-chart">
          {!snapshot ? (
            <div className="text-center py-10" data-testid="treatment-no-snapshot">
              <div className="w-12 h-12 rounded-full bg-amber-50 mx-auto flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h3 className="mt-3 text-base font-semibold text-slate-900">No chart yet</h3>
              <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                Finalize a chart in the Charting tab first — Treatment paints over the most recent chart.
              </p>
            </div>
          ) : (
            <>
              {isDraft && armedActive ? (() => {
                // Per-mode banner colors + copy. The legacy code conflated
                // multi-tooth into the "Tap to remove" branch (eraser) —
                // wrong text, no escape hatch. Each mode now has its own
                // banner color, accurate guidance, and an explicit Cancel
                // button so the doctor can always back out without losing
                // the rest of the chart context.
                const isProc   = armed.kind === "procedure";
                const isMulti  = armed.kind === "multi_tooth";
                const isErase  = armed.kind === "erase";
                const tone =
                  isProc  ? "bg-teal-50 border-teal-200 text-teal-800"
                  : isMulti ? "bg-violet-50 border-violet-200 text-violet-800"
                  : "bg-rose-50 border-rose-200 text-rose-800";
                const cancelBtnTone =
                  isProc  ? "text-teal-700 hover:text-teal-900"
                  : isMulti ? "text-violet-700 hover:text-violet-900"
                  : "text-rose-700 hover:text-rose-900";
                const text =
                  isProc  ? "Tap a tooth or surface to add this procedure to the plan."
                  : isMulti ? `Tap adjacent teeth on the chart to build a Bridge / Denture / RPD. ${multiToothBuffer.length === 0 ? "Pick at least 2 teeth, then click Define." : `Selected: ${multiToothBuffer.length}. Click Define when ready.`}`
                  : "Tap an item on the chart to remove it from the plan.";
                return (
                  <div className={`mb-3 px-3 py-2 rounded-lg border text-xs flex items-center justify-between gap-3 ${tone}`}>
                    <span>{text}</span>
                    <button
                      type="button"
                      onClick={() => { setArmed(null); setMultiToothBuffer([]); }}
                      data-testid="treatment-armed-cancel"
                      className={`shrink-0 text-xs font-semibold underline-offset-2 hover:underline ${cancelBtnTone}`}
                    >
                      Cancel
                    </button>
                  </div>
                );
              })() : null}
              <ToothChart
                state={txChartBaseState}                          // initial chart findings as subdued context
                overlayState={txState}                            // painted plan items layered above the chart
                overlayFindings={combinedFindings}                 // procedure-color catalog for overlayState
                traceState={null}
                findings={combinedFindings}                       // real catalog + synthetic procedure entries
                mode={dentitionMode}
                viewMode="full"
                active={isDraft && armedActive ? { kind: armed.kind === "erase" ? "erase" : "finding", code: "_tx" } : null}
                readOnly={!isDraft || !armedActive}
                canEditNote={isDraft && canEdit}
                isTxMode={true}
                dimFindings={true}
                multiToothGroups={multiToothGroups}
                onApplyFindingSurface={onApplyToSurface}
                onEraseSurface={(fdi, surface) => onApplyToSurface(fdi, surface)}
                onClearTooth={onClearTooth}
                onOpenNote={(fdi) => setNoteModalTooth(String(fdi))}
              />
              <ChartFindingsLegend
                state={txChartBaseState}
                findings={combinedFindings}
                label="Initial Charting"
                ariaLabel="Initial charting findings shown faintly on the Tx Plan chart"
              />
              <TxPlanCategoryLegend
                items={activePlan?.items ?? []}
                procedures={procedures}
              />
              {multiToothLegend.length > 0 ? (
                <div
                  className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2 py-2 rounded-lg bg-slate-50 border border-slate-200"
                  data-testid="multi-tooth-legend"
                  aria-label="Multi-tooth procedures on this plan"
                >
                  <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    Multi-tooth
                  </span>
                  {multiToothLegend.map((entry) => (
                    <span
                      key={entry.label}
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700"
                      data-testid={`multi-tooth-legend-${entry.label}`}
                    >
                      <span
                        aria-hidden
                        className="inline-block w-3 h-3 rounded-sm border"
                        style={{
                          backgroundColor: `${entry.color}1a`,
                          borderColor: entry.color,
                          borderWidth: 2,
                        }}
                      />
                      <span style={{ color: entry.color, letterSpacing: "0.04em" }}>
                        {entry.label}
                      </span>
                    </span>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Builder lives inside the left column directly under the
            chart card so it consumes the dead space the right palette
            otherwise creates. Mirrors Doctor.jsx's belowChartCanvas
            slot. The standalone bottom render below only fires in
            chartless mode (Doctor chairside Tx Plan). */}
        <TxPlanBuilder
          plan={activePlan}
          busy={busy}
          canPropose={canPropose}
          canAccept={canAccept}
          canEdit={canEdit}
          onDeleteItem={onDeleteItem}
          onSetItemStatus={onSetItemStatus}
          onSetItemDiscount={onSetItemDiscount}
          onSetItemPrice={onSetItemPrice}
          onSetItemNote={onSetItemNote}
          patient={patient}
          patientName={patient ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() : null}
          procedures={procedures}
          chairSetupMinutes={chairSetupMinutes}
          onReload={refreshActive}
          onPropose={onPropose}
          onResendEmail={onResendEmail}
          onRevise={onRevise}
          onDeleteDraftPlan={onDeleteDraftPlan}
          onScheduleItem={(it) => setScheduleFor(it)}
          onUnscheduleItem={onUnscheduleItem}
          onSetItemPhase={onSetItemPhase}
          onSetToothPhase={onSetToothPhase}
          onRenamePhase={onRenamePhase}
          onDeletePhase={onDeletePhase}
          onAddPhase={onAddPhase}
          onSchedulePhase={onSchedulePhase}
          onSetPhaseTiming={onSetPhaseTiming}
          onSetVisitPhase={tutorialDemoData?.handlers?.onSetVisitPhase}
          onSetToothNote={onSetToothNote}
          onOpenNote={(fdi) => setNoteModalTooth(String(fdi))}
          onDownloadPdf={onDownloadPdf}
          onSignDocument={onOpenSignDocument}
          signatureDocument={signatureDocument}
          signatureBusy={signatureBusy}
          onComparePlans={() => setCompareOpen(true)}
          onMarkAccepted={(acceptedIds) => { setPendingAcceptedIds(acceptedIds ?? null); setMarkAcceptedOpen(true); }}
          onOpenPlan={setActivePlanId}
          onClosePlan={() => setCloseOpen(true)}
          onCollectPayment={() => setCollectOpen(true)}
          onBuildMultiTooth={hideMultiTooth ? null : () => setMultiToothOpen(true)}
          planBalance={planBalance}
          canCompare={(plans?.length ?? 0) >= 2}
          readOnlyActions={tutorialDemoMode}
          undoCount={undoOps.length}
          redoCount={redoOps.length}
          undoBusy={undoBusy}
          onUndo={onUndoTx}
          onRedo={onRedoTx}
          onVisitStagesChanged={() => refreshActive()}
        />
        <PdfDoctorProfileError error={pdfDoctorError} />
        </div>

        {isDraft ? (
          <div className="hidden xl:block xl:sticky xl:top-4 xl:self-start">
            <ProceduresPalette
              procedures={procedures}
              active={armed}
              disabled={!canEdit}
              onPickProcedure={(id) => setArmed((a) => (a?.kind === "procedure" && a.id === id) ? null : { kind: "procedure", id })}
              onPickWholeMouth={onPickWholeMouth}
              onAddMissingProcedure={(name) => setAddProcedureOpen({ name })}
              onPickEraser={() => setArmed((a) => (a?.kind === "erase") ? null : { kind: "erase" })}
              onClear={() => { setArmed(null); setMultiToothBuffer([]); }}
              onPickMultiTooth={() => {
                setArmed((a) => (a?.kind === "multi_tooth") ? null : { kind: "multi_tooth" });
                setMultiToothBuffer([]);
              }}
              onDefineMultiTooth={() => setMultiToothOpen(true)}
              multiToothCount={multiToothBuffer.length}
              showMultiTooth={!hideMultiTooth}
            />
          </div>
        ) : null}
      </div>
      )}

      {!chartless && chartFirst ? (
        <>
          {planTabsCard}
          <MedicalHistoryBanner mh={medicalHistory} />
        </>
      ) : null}

      {/* Standalone bottom builder — only used in chartless mode
          (Doctor chairside Tx Plan, where the chart row is hidden and
          there's no left-column to nest under). When the chart is
          shown, the builder lives inside the left column above. */}
      {chartless ? (
      <>
        <TxPlanBuilder
          plan={activePlan}
          busy={busy}
          canPropose={canPropose}
          canAccept={canAccept}
          canEdit={canEdit}
          onDeleteItem={onDeleteItem}
          onSetItemStatus={onSetItemStatus}
          onSetItemDiscount={onSetItemDiscount}
          onSetItemPrice={onSetItemPrice}
          onSetItemNote={onSetItemNote}
          patient={patient}
          patientName={patient ? `${patient.first_name || ""} ${patient.last_name || ""}`.trim() : null}
          procedures={procedures}
          chairSetupMinutes={chairSetupMinutes}
          onReload={refreshActive}
          onPropose={onPropose}
          onResendEmail={onResendEmail}
          onRevise={onRevise}
          onDeleteDraftPlan={onDeleteDraftPlan}
          onScheduleItem={(it) => setScheduleFor(it)}
          onUnscheduleItem={onUnscheduleItem}
          onSetItemPhase={onSetItemPhase}
          onSetToothPhase={onSetToothPhase}
          onRenamePhase={onRenamePhase}
          onDeletePhase={onDeletePhase}
          onAddPhase={onAddPhase}
          onSchedulePhase={onSchedulePhase}
          onSetPhaseTiming={onSetPhaseTiming}
          onSetVisitPhase={tutorialDemoData?.handlers?.onSetVisitPhase}
          onSetToothNote={onSetToothNote}
          onOpenNote={(fdi) => setNoteModalTooth(String(fdi))}
          onDownloadPdf={onDownloadPdf}
          onSignDocument={onOpenSignDocument}
          signatureDocument={signatureDocument}
          signatureBusy={signatureBusy}
          onComparePlans={() => setCompareOpen(true)}
          onMarkAccepted={(acceptedIds) => { setPendingAcceptedIds(acceptedIds ?? null); setMarkAcceptedOpen(true); }}
          onOpenPlan={setActivePlanId}
          onClosePlan={() => setCloseOpen(true)}
          onCollectPayment={() => setCollectOpen(true)}
          // Chairside Multi-Tooth flow lives on the chart canvas now —
          // hide the dialog-trigger button in the doctor's embedded
          // builder so the only entry point is the chart's Multi-Tooth
          // tool. Patient-tab Tx Plan keeps the button.
          onBuildMultiTooth={null}
          planBalance={planBalance}
          canCompare={(plans?.length ?? 0) >= 2}
          readOnlyActions={tutorialDemoMode}
          undoCount={undoOps.length}
          redoCount={redoOps.length}
          undoBusy={undoBusy}
          onUndo={onUndoTx}
          onRedo={onRedoTx}
          onVisitStagesChanged={() => refreshActive()}
        />
        <PdfDoctorProfileError error={pdfDoctorError} />
      </>
      ) : null}

      {/* CaseTimelineCard is now embedded into each ItemRow via the
          VisitTimelineStrip in TxPlanBuilder. The standalone card is
          retired to avoid showing the same procedure twice (once as
          a plan item, once as a timeline row). */}

      <SchedulePlanItemDialog
        open={!!scheduleFor}
        onOpenChange={(v) => { if (!v) setScheduleFor(null); }}
        item={scheduleFor}
        plan={activePlan}
        phaseNumber={scheduleFor?.phase_number ?? null}
        patientId={patientId}
        onScheduled={(res) => {
          if (res && !res.partial && res.items?.length) {
            const ids = res.items.map((i) => i.id);
            const apptId = res.apptId;
            recordOp({
              label: "Schedule item",
              undo: async () => { for (const id of ids) await supabase.rpc("unschedule_plan_item", { p_item_id: id }); },
              redo: async () => { for (const id of ids) await supabase.rpc("schedule_plan_item",   { p_item_id: id, p_appointment_id: apptId }); },
            });
          }
          setScheduleFor(null);
          refreshActive();
        }}
      />

      <SchedulePlanItemDialog
        open={!!schedulePhase}
        onOpenChange={(v) => { if (!v) setSchedulePhase(null); }}
        items={schedulePhase?.items}
        title={schedulePhase?.title}
        plan={activePlan}
        phaseNumber={schedulePhase?.phaseNumber}
        patientId={patientId}
        onScheduled={(res) => {
          if (res && !res.partial && res.items?.length) {
            const ids = res.items.map((i) => i.id);
            const apptId = res.apptId;
            recordOp({
              label: `Schedule ${ids.length} items`,
              undo: async () => { for (const id of ids) await supabase.rpc("unschedule_plan_item", { p_item_id: id }); },
              redo: async () => { for (const id of ids) await supabase.rpc("schedule_plan_item",   { p_item_id: id, p_appointment_id: apptId }); },
            });
          }
          setSchedulePhase(null);
          refreshActive();
        }}
      />

      <ComparePlansDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        currentPlanId={activePlanId}
        plans={plans}
        onCompare={onComparePlans}
      />

      <MarkAcceptedDialog
        open={markAcceptedOpen}
        onOpenChange={(v) => { setMarkAcceptedOpen(v); if (!v) setPendingAcceptedIds(undefined); }}
        initialAcceptedIds={pendingAcceptedIds ?? null}
        schedulingOnly={pendingAcceptedIds !== undefined}
        plan={activePlan}
        patientId={patientId ?? activePlan?.patient_id ?? null}
        onAccepted={(result) => {
          // Lifecycle v2: accept_plan succeeded.
          // - path='schedule' → patient has a date; chain into the
          //   visit-1 scheduler with phase-1 items pre-loaded.
          // - path='queue'    → no date yet; just refresh and let the
          //   plan land in the Needs Booking queue.
          refreshActive();
          if (result?.path === "start_today") {
            onPlanStartedToday?.(result);
            return;
          }
          if (result?.path === "schedule") {
            const phaseOneItems = (activePlan?.items ?? []).filter(
              (i) =>
                i.status !== "Cancelled" &&
                i.status !== "Declined" &&
                Number(i.phase_number || 1) === 1,
            );
            if (phaseOneItems.length > 0) {
              setSchedulePhase({
                items: phaseOneItems,
                title: "Schedule Visit 1",
                phaseNumber: 1,
              });
            }
          }
        }}
        startTodayAppointmentId={currentAppointmentId}
      />

      <SignatureDialog
        open={!!signingPlan}
        onOpenChange={(next) => {
          if (!next && !signatureBusy) setSigningPlan(null);
        }}
        context={{
          title: signingPlan?.status === "Proposed"
            ? "Sign and accept treatment plan"
            : "Sign treatment plan",
          documentNumber: signingPlan?.plan_code || signingPlan?.id,
          patientName: patient
            ? [patient.first_name, patient.last_name].filter(Boolean).join(" ") || patient.full_name
            : null,
        }}
        defaultSignerName={patient
          ? [patient.first_name, patient.last_name].filter(Boolean).join(" ") || patient.full_name
          : ""}
        busy={signatureBusy}
        onSubmit={onSubmitSignature}
      />

      <ClosePlanDialog
        open={closeOpen}
        onOpenChange={setCloseOpen}
        plan={activePlan}
        balanceCentavos={Number(planBalance?.balance_centavos) || 0}
        onClosed={() => { refreshActive(); }}
      />

      <CollectPaymentDialog
        open={collectOpen}
        onOpenChange={setCollectOpen}
        planId={activePlan?.id}
        balanceCentavos={Number(planBalance?.balance_centavos) || 0}
        label={activePlan?.status === "Completed" ? "Collect from AR" : "Collect Payment"}
        onCollected={() => { refreshActive(); }}
      />

      <BuildMultiToothDialog
        open={multiToothOpen}
        onOpenChange={(v) => {
          setMultiToothOpen(v);
          // Closing the dialog (cancel) keeps the chart selection so
          // the dentist can keep tweaking. Successful add clears below.
        }}
        planId={activePlan?.id}
        procedures={procedures}
        presetTeeth={multiToothBuffer}
        missingFdiSet={missingFdiSet}
        onAdded={() => {
          setMultiToothBuffer([]);
          setArmed(null);
          refreshActive();
        }}
      />

      <ToothNoteDialog
        fdi={noteModalTooth}
        initialText={noteModalTooth ? (activePlan?.tooth_notes?.[noteModalTooth] || "") : ""}
        contextLine={noteModalTooth ? toothContextLine(activePlan, noteModalTooth) : null}
        chartNote={noteModalTooth ? (chartNotesByFdi[noteModalTooth] || null) : null}
        onSave={(text) => onSetToothNote(noteModalTooth, text)}
        onDelete={() => onSetToothNote(noteModalTooth, null)}
        onClose={() => setNoteModalTooth(null)}
      />

      {/* Mobile palette — show as a card below builder when narrow.
          Bottom-sheet pattern can come in a polish round. Hidden in
          chartless mode because the chairside ProceduresPalette
          (workspace right rail / mobile sheet) is already mounted
          and rendering this would duplicate the picker on tablet. */}
      {isDraft && !isDesktop && !chartless ? (
        <ProceduresPalette
          procedures={procedures}
          active={armed}
          disabled={!canEdit}
          onPickProcedure={(id) => setArmed((a) => (a?.kind === "procedure" && a.id === id) ? null : { kind: "procedure", id })}
          onPickWholeMouth={onPickWholeMouth}
          onAddMissingProcedure={(name) => setAddProcedureOpen({ name })}
          onPickEraser={() => setArmed((a) => (a?.kind === "erase") ? null : { kind: "erase" })}
          onClear={() => { setArmed(null); setMultiToothBuffer([]); }}
          onPickMultiTooth={() => {
            setArmed((a) => (a?.kind === "multi_tooth") ? null : { kind: "multi_tooth" });
            setMultiToothBuffer([]);
          }}
          onDefineMultiTooth={() => setMultiToothOpen(true)}
          multiToothCount={multiToothBuffer.length}
          showMultiTooth={!hideMultiTooth}
        />
      ) : null}

      {plans.length === 0 ? (
        <div className="bg-white rounded-2xl shadow-sm p-8 text-center" data-testid="treatment-empty-state">
          <div className="w-12 h-12 rounded-full bg-teal-50 mx-auto flex items-center justify-center">
            <Activity className="w-5 h-5 text-teal-600" />
          </div>
          <h3 className="mt-3 text-base font-semibold text-slate-900">No treatment plans yet</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
            Tap <span className="font-semibold">+ New Plan</span> above to start building one.
          </p>
        </div>
      ) : null}

      <AddProcedureToPlanDialog
        open={!!addProcedureOpen}
        initialName={addProcedureOpen?.name || ""}
        busy={!!busy.add_procedure_to_plan}
        onClose={() => setAddProcedureOpen(null)}
        onSubmit={onCreateProcedureAndAddToPlan}
      />
    </div>
  );
});

const TreatmentTab = forwardRef(function TreatmentTab(props, ref) {
  return (
    <FeatureAccessGate featureKey="tx_plan_center">
      <TreatmentTabBody {...props} ref={ref} />
    </FeatureAccessGate>
  );
});

export default TreatmentTab;

function AddProcedureToPlanDialog({ open, initialName, busy, onClose, onSubmit }) {
  const [name, setName] = useState(initialName || "");
  const [shortCode, setShortCode] = useState("");
  const [category, setCategory] = useState("");
  const [feePesos, setFeePesos] = useState("");
  const [duration, setDuration] = useState("");
  const [chartPlacement, setChartPlacement] = useState("whole_tooth");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setName(initialName || "");
    setShortCode("");
    setCategory("");
    setFeePesos("");
    setDuration("");
    setChartPlacement("whole_tooth");
    setError(null);
  }, [initialName, open]);

  if (!open) return null;

  async function handleSubmit() {
    if (!name.trim()) {
      setError("Enter a procedure name.");
      return;
    }
    if (!category) {
      setError("Pick a category from the approved list.");
      return;
    }
    if (feePesos === "" || Number(feePesos) < 0) {
      setError("Enter a default fee. Use 0 if this service is free.");
      return;
    }
    setError(null);
    await onSubmit?.({
      name,
      shortCode,
      category,
      feePesos,
      duration,
      chartPlacement,
    });
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/30 flex items-center justify-center p-4" data-testid="tx-plan-add-service-dialog">
      <div className="w-full max-w-lg rounded-xl bg-white shadow-xl border border-slate-200">
        <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">Add to my services</h3>
            <p className="text-xs text-slate-500 mt-0.5">Creates a normal clinic service, then adds it to this draft plan.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-name">
              Name <span className="text-rose-500">*</span>
            </label>
            <input
              id="tx-plan-new-procedure-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="tx-plan-new-procedure-name"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
              autoFocus
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-category">
                Category <span className="text-rose-500">*</span>
              </label>
              <select
                id="tx-plan-new-procedure-category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                data-testid="tx-plan-new-procedure-category"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded bg-white focus:outline-none focus:border-teal-500"
              >
                <option value="">Pick category...</option>
                {APPROVED_PROCEDURE_CATEGORIES.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-fee">
                Default fee <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <span className="absolute left-2 top-1.5 text-sm text-slate-500">₱</span>
                <input
                  id="tx-plan-new-procedure-fee"
                  type="number"
                  min="0"
                  step="0.01"
                  value={feePesos}
                  onChange={(e) => setFeePesos(e.target.value)}
                  data-testid="tx-plan-new-procedure-fee"
                  className="w-full pl-6 pr-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
                />
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-short-code">
                Short code
              </label>
              <input
                id="tx-plan-new-procedure-short-code"
                value={shortCode}
                onChange={(e) => setShortCode(e.target.value.toUpperCase())}
                data-testid="tx-plan-new-procedure-short-code"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500 font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-duration">
                Raw work time
              </label>
              <input
                id="tx-plan-new-procedure-duration"
                type="number"
                min="1"
                max="480"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                data-testid="tx-plan-new-procedure-duration"
                className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1" htmlFor="tx-plan-new-procedure-placement">
              Chart placement
            </label>
            <select
              id="tx-plan-new-procedure-placement"
              value={chartPlacement}
              onChange={(e) => setChartPlacement(e.target.value)}
              data-testid="tx-plan-new-procedure-placement"
              className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded bg-white focus:outline-none focus:border-teal-500"
            >
              <option value="whole_tooth">Whole tooth</option>
              <option value="surface">Tooth surfaces</option>
              <option value="whole_mouth">Whole mouth / no tooth highlight</option>
            </select>
          </div>

          {error ? (
            <p className="text-xs text-rose-600" data-testid="tx-plan-add-service-error">{error}</p>
          ) : null}
        </div>

        <div className="px-4 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-xs font-semibold rounded border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={busy}
            data-testid="tx-plan-add-service-save"
            className="px-3 py-2 text-xs font-semibold rounded bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Add service
          </button>
        </div>
      </div>
    </div>
  );
}

function PdfDoctorProfileError({ error }) {
  if (!error?.message) return null;
  return (
    <div
      className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
      data-testid="tx-plan-pdf-doctor-error"
      role="alert"
    >
      <span>{error.message}</span>
      {error.href ? (
        <Link className="ml-2 font-semibold underline decoration-dotted hover:no-underline" to={error.href}>
          Edit profile
        </Link>
      ) : null}
    </div>
  );
}

// =====================================================================
// MedicalHistoryBanner — liability gate.
//   * No row yet     → red "Capture before treating"
//   * Submitted      → amber "Awaiting verification"
//   * Verified+expired → red "Expired — capture a new one"
//   * Verified+current → no banner (silent green)
// All states except the last render a deep-link to the Medical
// History tab. Verified+current is intentionally silent so the
// dentist isn't reminded of safety state they're already in.
// =====================================================================
function MedicalHistoryBanner({ mh }) {
  if (mh && mh.status === "Verified" && mh.is_current) return null;

  let tone, title, hint;
  if (!mh) {
    tone = "rose";
    title = "No medical history on file.";
    hint  = "Capture allergies, medications, and conditions before proposing treatment.";
  } else if (mh.status === "Verified" && !mh.is_current) {
    tone = "rose";
    title = "Medical history has expired.";
    hint  = "The validity window has passed. Capture a fresh history before treatment.";
  } else if (mh.status === "Submitted") {
    tone = "amber";
    title = "Medical history submitted but not verified.";
    hint  = "A doctor or clinic admin should verify before treatment.";
  } else {
    tone = "amber";
    title = "Medical history is a draft.";
    hint  = "Submit and verify the medical history before proposing treatment.";
  }

  const cls = tone === "rose"
    ? "bg-rose-50 border-rose-200 text-rose-900"
    : "bg-amber-50 border-amber-200 text-amber-900";
  const Icon = tone === "rose" ? AlertTriangle : AlertTriangle;
  const iconCls = tone === "rose" ? "text-rose-500" : "text-amber-600";

  return (
    <div
      className={`${cls} border rounded-xl p-3 flex items-start gap-2.5`}
      data-testid="treatment-mh-banner"
    >
      <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconCls}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold">{title}</p>
        <p className="text-xs mt-0.5 opacity-80">{hint}</p>
      </div>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          const ev = new CustomEvent("dos-jump-to-tab", { detail: "medical-history" });
          window.dispatchEvent(ev);
        }}
        className="text-xs font-semibold underline decoration-dotted hover:no-underline shrink-0 self-center"
        data-testid="treatment-mh-banner-link"
      >
        Open Medical History →
      </a>
    </div>
  );
}
